'use client';

import { sendMagicLink } from '@/lib/magic-link';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Input } from '@tajdid/tajdid-ui';
import { PhoneIcon } from '@tajdid/tajdid-ui/icons';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const emailSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type EmailSchema = z.infer<typeof emailSchema>;

type MagicLinkFormProps = {
  onSuccess: () => void;
  hasOtp: boolean;
  setActiveMethod: (method: 'magic' | 'otp') => void;
};

export function MagicLinkForm({
  onSuccess,
  hasOtp,
  setActiveMethod,
}: MagicLinkFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchParams = useSearchParams();
  const redirectUrl = searchParams.get('redirect');
  const promo = searchParams.get('promo');
  const t = useTranslations();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailSchema>({
    resolver: zodResolver(emailSchema),
  });

  async function onSubmit(data: EmailSchema) {
    try {
      setIsLoading(true);
      const urlParams = new URLSearchParams();

      if (redirectUrl) {
        urlParams.append('redirect', redirectUrl);
      }

      if (promo) {
        urlParams.append('promo', promo);
      }

      await sendMagicLink(data.email, urlParams.toString());
      setIsLoading(false);
      onSuccess();
    } catch (error) {
      setIsLoading(false);
      setError('Failed to send magic link. Please try again.');
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid gap-6">
      <div>
        <h1 className="text-4xl font-bold text-center text-gray-800">
          {t('login.title')}
        </h1>
      </div>
      <div className="w-full max-w-md px-4 mx-auto">
        <div className="grid gap-6">
          <Input
            label={t('profile-page.email')}
            {...register('email')}
            error={errors.email?.message}
            placeholder="example@example.com"
            aria-invalid={errors.email ? 'true' : 'false'}
          />
          {error && <p className="text-sm text-red-500">{error}</p>}
          <Button
            isLoading={isLoading}
            className="w-full"
            type="submit"
            disabled={isLoading}
          >
            {t('login.continue')}
          </Button>
          {hasOtp && (
            <div className="relative flex items-center justify-center">
              <div className="absolute w-full border-t border-gray-300"></div>
              <span className="relative px-4 text-sm text-gray-500 bg-white">
                Or continue with
              </span>
            </div>
          )}
          {hasOtp && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setActiveMethod('otp')}
              icon={<PhoneIcon className="w-5 h-5" />}
              iconPosition="start"
              className="w-full"
            >
              {t('login.mobile-otp')}
            </Button>
          )}
        </div>
      </div>
    </form>
  );
}
